import { useMemo } from 'react';
import { useGetBettieBalances } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieBalances';
import { useGetBettieBalanceTransaction } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieBalanceTransaction';
import { transformDateToInputValue } from '@pocapan/shared/utils/DateUtil';
import type { GetBettieBalanceTransactionsResponse } from '@pocapan/shared/domains/bettie/services/bettieBalancesService';
import type { components } from '@pocapan/shared/types/schema';

type BettieBalancesTransactions = components['schemas']['ApiV1BettieTransaction'][];

export const useBettieBalancesTransactions = () => {
  const { data: bettieBalance, isLoading: isBettieBalancesLoading } = useGetBettieBalances();
  const {
    data: bettieBalanceTransactions,
    isLoading: isBettieBalanceTransactionsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useGetBettieBalanceTransaction();

  const bettieTransactions = useMemo(() => {
    const transactions = (bettieBalanceTransactions?.pages ?? []).flatMap(
      (p: GetBettieBalanceTransactionsResponse) => p.bettieTransactions ?? []
    );
    const map = new Map<string, BettieBalancesTransactions>();
    for (const transaction of transactions ?? []) {
      const key = transformDateToInputValue(transaction.occurredAt).replace(/-/g, '.');
      const arr = map.get(key) ?? [];
      arr.push(transaction);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [bettieBalanceTransactions]);

  const totalBettieBalance = useMemo(() => {
    return bettieBalance?.bettiePoint?.balance ?? 0;
  }, [bettieBalance]);

  const isLoading = isBettieBalancesLoading || isBettieBalanceTransactionsLoading;

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return { totalBettieBalance, bettieTransactions, isLoading, loadMore };
};
